import React from 'react'
import { useLocation, useRouteMatch } from '@fs/zion-router'
import zionDebug from '@fs/zion-debug'
import { css } from '@emotion/react'
import { BillboardButton, Row } from '@fs/zion-ui'
import { baseUrl, sgBaseUrl } from '@fs/zion-config'

import { useBrightspot } from '../BrightspotProvider'
import BrightspotPage from '../BrightspotPage'
import BrightspotPreview from '../BrightspotPreview'
import useUrlPreviewIdTypename from '../../hooks/useUrlPreviewIdTypename'
import BrightspotRoutingProvider from '../../providers/BrightspotProviders/BrightspotRoutingProvider'

const debug = zionDebug('zion-cms')
const analyzeCss = css`
  border: dashed 3px fuchsia;
  borderradius: 5px;
  text-align: center;
`

const previewTypenameList = ['Overlay', 'Popover', 'SharedModulesContent', 'SharedSectionsContent']

type BrightspotRoutingProps = {
  /**
   * The Brightspot site that ZionCMSRouting will use to get the pages.
   */
  site: string
}

export default function BrightspotRouting({ site }: BrightspotRoutingProps): JSX.Element {
  // see https://reactrouter.com/web/example/nesting
  const { url } = useRouteMatch()
  const { search } = useLocation()
  const { sites, subsites } = useBrightspot()
  const { previewId, typename } = useUrlPreviewIdTypename()

  debug(`BrightspotRouting routeMatch:${url}, location:${search}, sites:${sites}`)

  if (!subsites.some((s) => s.indexOf(site) !== -1) && !sites.some((s) => s === site)) {
    throw new Error(`"${site}" is not part of the list of sites or subsites provided to ZionCMSProvider`)
  }

  if (previewId && typename && previewTypenameList.includes(typename)) {
    return <BrightspotPreview previewId={previewId} typename={typename} site={site} />
  }

  return (
    <BrightspotRoutingProvider basePath={url}>
      {previewId && (
        <div css={analyzeCss}>
          <Row>
            <BillboardButton
              color="green"
              emphasis="lightHigh"
              external
              target="_blank"
              aria-label="Check Accessibility (opens in new tab)"
              to={`https://wave.webaim.org/report#/${getPrerenderUrl()}`}
            >
              Check Accessibility
            </BillboardButton>
            <BillboardButton
              color="green"
              emphasis="lightHigh"
              external
              target="_blank"
              aria-label="Check Performance (opens in new tab)"
              to={`https://pagespeed.web.dev/analysis?url=${window.location.href}`}
            >
              Check Performance
            </BillboardButton>
            <BillboardButton
              color="green"
              external
              target="_blank"
              aria-label="Preview Social Share (opens in new tab)"
              emphasis="lightHigh"
              to={`https://socialsharepreview.com/?url=${getPrerenderUrl()}`}
            >
              Preview Social Share
            </BillboardButton>
            <BillboardButton
              color="green"
              external
              target="_blank"
              aria-label="Preview Search Engine (opens in new tab)"
              emphasis="lightHigh"
              to={`https://metatags.io/?url=${encodeURIComponent(getPrerenderUrl())}`}
            >
              Preview Search Engine
            </BillboardButton>
          </Row>
        </div>
      )}

      <BrightspotPage site={site} trimUrl={url} />
    </BrightspotRoutingProvider>
  )
}

function getPrerenderUrl() {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const previewId = urlSearchParams.get('previewId') || urlSearchParams.get('_cms.db.previewId')
  const typename = urlSearchParams.get('typename')

  // Get the current path without query params
  const currentPath = window.location.pathname

  // Build the full preview URL with the current environment's base URL
  const previewUrl = `${baseUrl}${currentPath}__preview__/${previewId}/type/${typename}`

  // Use the pre-render service that is NOT blocked by login
  // Format: https://sg30p0.familysearch.org/frontier/prerender/https://www.familysearch.org/en/library/__preview__/...
  const url = `${sgBaseUrl}/frontier/prerender/${previewUrl}`

  return url
}
